import { observer } from 'mobx-react'
import { type FC, useContext, useState } from 'react'
import { Button, Icon, LoadingSpinner, OtpInput } from '@components/Generic'
import { Header } from '@components/Generic/Header/Header'
import type { EasySignGetContextResponse } from '@components/SignDocumentCommon/types'
import { API_URL, EASYSIGN_SERVICE_PATH } from '@constants/apiUrl'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'
import { StoreContext } from '@store/Root.store'
import { cx } from '@utils/cx'
import { postSendOtp } from '../methods'

interface VerifyOtpCodeProps {
  otpContext: EasySignGetContextResponse['smsOtp']
  onError: () => void
  resendCooldown: number
  setResendCooldown: (seconds: number) => void
  attemptsRemaining: number | null
  setAttemptsRemaining: (attempts: number | null) => void
  navigateToChangePhoneNumber: () => void
}

export const VerifyOtpCode: FC<VerifyOtpCodeProps> = observer(
  ({
    otpContext,
    onError,
    resendCooldown,
    setResendCooldown,
    attemptsRemaining,
    setAttemptsRemaining,
    navigateToChangePhoneNumber,
  }) => {
    const store = useContext(StoreContext)
    const { verifyOtpStep: trans } =
      store.TranslationsState.translations.easySignSmsOtpVerificationPage

    const [isSendOtpLoading, setIsSendOtpLoading] = useState(false)
    const [isVerifyOtpLoading, setIsVerifyOtpLoading] = useState(false)

    const [otpValue, setOtpValue] = useState('')
    const [isCodeIncorrect, setIsCodeIncorrect] = useState(false)
    const [isCodeExpired, setIsCodeExpired] = useState(false)
    const shouldDisableButtons = isSendOtpLoading || isVerifyOtpLoading
    const isOtpFullyEntered = otpValue.length === 6

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && isOtpFullyEntered && !shouldDisableButtons) {
        verifyOtp()
      }
    }

    const verifyOtp = async () => {
      try {
        setIsVerifyOtpLoading(true)
        const res = await axiosInstance.post<{
          isVerified: boolean
          isExpired: boolean
          attemptsRemaining: number
        }>(
          `${API_URL}/${EASYSIGN_SERVICE_PATH}/sms-otp/verify`,
          {
            otp: otpValue,
          },
          {
            withCredentials: true,
          }
        )

        if (res.data.isVerified) {
          store.ScenarioState.nextPage()
        } else if (res.data.isExpired) {
          setIsCodeExpired(true)
          setIsCodeIncorrect(false)
          setAttemptsRemaining(res.data.attemptsRemaining)

          if (res.data.attemptsRemaining === 0) {
            onError()
          } else {
            setIsVerifyOtpLoading(false)
          }
        } else {
          setIsCodeIncorrect(true)
          setIsCodeExpired(false)
          setAttemptsRemaining(res.data.attemptsRemaining)

          if (res.data.attemptsRemaining === 0) {
            onError()
          } else {
            setIsVerifyOtpLoading(false)
          }
        }
      } catch (error) {
        devLog('Error verifying OTP:', error)
        setIsVerifyOtpLoading(false)
      }
    }

    return (
      <div className="page-container horizontally-centered" onKeyDown={handleKeyDown}>
        <Header className="m-20-0-0">{trans.header}</Header>
        <p className="generic__paragraph-centered">
          {trans.description} {otpContext?.phoneNumber}
        </p>

        <div className="generic__spacer h-20" />

        <OtpInput
          value={otpValue}
          valueLength={6}
          onChange={(value) => {
            setIsCodeIncorrect(false)
            setIsCodeExpired(false)
            setOtpValue(value)
          }}
          className={cx(
            'otp-input__container--full-width',
            'otp-input__container--mt-20',
            (isCodeIncorrect || isCodeExpired) && 'error',
            'no-margin'
          )}
        />

        {isCodeIncorrect && (
          <>
            <div className="generic__spacer h-15" />
            <div
              className={cx(
                'generic__paragraph-centered',
                'flex-row-center',
                'generic__error-message'
              )}
            >
              <Icon type="info" size="18px" color="#D83535" />
              {trans.incorrectCode}
            </div>
          </>
        )}

        {isCodeExpired && (
          <>
            <div className="generic__spacer h-15" />
            <div
              className={cx(
                'generic__paragraph-centered',
                'flex-row-center',
                'generic__error-message',
                'generic--text-left'
              )}
            >
              <Icon type="info" size="18px" color="#D83535" />
              {trans.expiredCode}
            </div>
          </>
        )}

        {/* requested to hide attempts remaining message when expired error is shown */}
        {attemptsRemaining === 2 && !isCodeExpired && (
          <>
            <div className="generic__spacer h-15" />
            <div className={cx('generic__paragraph-centered', 'flex-row-center')}>
              <Icon type="info" size="18px" color="#3131D9" />
              {trans.attemptsLeft?.replace('<number>', attemptsRemaining.toString())}
            </div>
          </>
        )}

        {/* requested to hide attempts remaining message when expired error is shown */}
        {attemptsRemaining === 1 && !isCodeExpired && (
          <>
            <div className="generic__spacer h-15" />
            <div
              className={cx(
                'generic__paragraph-centered',
                'flex-row-center',
                'generic__warning-message'
              )}
            >
              <Icon type="warning" size="18px" color="#FFA500" />
              {trans.lastAttempt}
            </div>
          </>
        )}

        <div className="generic__spacer h-4" />

        {otpContext.isPhoneNumberEditable && (
          <Button
            className="button--link"
            onClick={navigateToChangePhoneNumber}
            disabled={resendCooldown > 0 || shouldDisableButtons}
          >
            <span className="generic__button-text">{trans.changePhoneNumber}</span>
          </Button>
        )}

        <div className="generic__spacer h-30" />

        <p className="generic__paragraph-centered">{trans.youHaventReceivedTheSms}</p>
        <Button
          onClick={async () => {
            if (resendCooldown > 0 || shouldDisableButtons) return

            if (otpContext?.phoneNumber) {
              setIsSendOtpLoading(true)

              try {
                await postSendOtp(otpContext.phoneNumber)
                setResendCooldown(otpContext?.resendCooldownSeconds || 30)
              } catch (error) {
                devLog('Error resending OTP:', error)
                setResendCooldown(5)
              } finally {
                setIsSendOtpLoading(false)
              }
            }
          }}
          disabled={resendCooldown > 0 || shouldDisableButtons}
          className="button--secondary"
        >
          <span className="generic__button-text">
            {resendCooldown > 0
              ? trans.sendSmsAgain?.replace('<number>', resendCooldown.toString())
              : trans.resendSms}
          </span>
        </Button>

        <div
          className={cx(
            'layout__content-container-bottom-fixed',
            'layout__content-container-bottom-fixed--border-top-none'
          )}
        >
          <Button
            disabled={
              !isOtpFullyEntered || shouldDisableButtons || isCodeIncorrect || isCodeExpired
            }
            onClick={
              shouldDisableButtons || isCodeIncorrect || isCodeExpired ? undefined : verifyOtp
            }
          >
            {shouldDisableButtons && <LoadingSpinner inButton />}
            <span
              className={cx(
                'generic__button-text',
                shouldDisableButtons && 'generic__button-text--loading'
              )}
            >
              {trans.proceed}
            </span>
          </Button>
        </div>
      </div>
    )
  }
)
